/**
 * HeroBanner Component
 * Netflix-style hero banner with featured course
 */

import type { Course } from '../../types/academy';
import { Icons } from '../Icons';

interface HeroBannerProps {
  course: Course;
  onPlay: () => void;
  onMoreInfo: () => void;
  onAddToList: () => void;
  isInList?: boolean;
}

export function HeroBanner({ course, onPlay, onMoreInfo, onAddToList, isInList = false }: HeroBannerProps) {
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i}>⭐</span>);
    }
    return stars;
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '600px',
        marginBottom: '32px',
        borderRadius: '12px',
        overflow: 'hidden',
        background: '#0f0f0f'
      }}
    >
      {/* Background Image */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${course.thumbnail})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.6)'
        }}
      />

      {/* Gradient Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.9) 100%), linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%)'
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 60px',
          maxWidth: '700px'
        }}
      >
        {/* Academy Badge */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            fontSize: '32px',
            fontWeight: 700,
            color: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>🎓</span>
            <span>Edouard.ai Academy</span>
          </div>
        </div>

        <div style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.8)',
          marginBottom: '24px',
          fontWeight: 600,
          letterSpacing: '1px',
          textTransform: 'uppercase'
        }}>
          Master Crypto Trading & Portfolio Management
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: '48px',
            fontWeight: 700,
            marginBottom: '20px',
            lineHeight: '1.2',
            textShadow: '2px 2px 8px rgba(0,0,0,0.8)'
          }}
        >
          {course.title}
        </h1>

        {/* Meta Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          fontSize: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {renderStars(course.rating)}
            <span style={{ marginLeft: '6px', fontWeight: 600 }}>{course.rating.toFixed(1)}</span>
            <span style={{ color: 'rgba(255,255,255,0.6)', marginLeft: '4px' }}>
              ({course.ratingCount.toLocaleString()})
            </span>
          </div>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>•</span>
          <div style={{ fontWeight: 600 }}>{course.duration}m</div>
          {course.lessons && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>•</span>
              <div style={{ fontWeight: 600 }}>{course.lessons} lessons</div>
            </>
          )}
          {course.isPro && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>•</span>
              <div style={{
                padding: '4px 12px',
                borderRadius: '6px',
                background: 'var(--accent)',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600
              }}>
                PRO
              </div>
            </>
          )}
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: '18px',
            lineHeight: '1.6',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '32px',
            maxWidth: '600px',
            textShadow: '1px 1px 4px rgba(0,0,0,0.8)'
          }}
        >
          {course.description}
        </p>

        {/* Instructor */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '32px',
          fontSize: '15px',
          color: 'rgba(255,255,255,0.8)'
        }}>
          <span>👨‍🏫</span>
          <span style={{ fontWeight: 600 }}>{course.instructor}</span>
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={onPlay}
            className="btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              fontSize: '18px',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
            }}
          >
            <Icons.Play size={24} />
            Watch Now
          </button>

          <button
            onClick={onAddToList}
            className="btn-ghost"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              fontSize: '16px',
              fontWeight: 600,
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {isInList ? (
              <>
                <Icons.Check size={20} />
                In My List
              </>
            ) : (
              <>
                <Icons.Plus size={20} />
                My List
              </>
            )}
          </button>

          <button
            onClick={onMoreInfo}
            className="btn-ghost"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 28px',
              fontSize: '16px',
              fontWeight: 600,
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Icons.Info size={20} />
            More Info
          </button>
        </div>
      </div>

      {/* Bottom fade */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100px',
          background: 'linear-gradient(180deg, transparent 0%, #0f0f0f 100%)',
          pointerEvents: 'none'
        }}
      />
    </div>
  );
}
